/**
 * marketplaceRowToNoteRecord — החוליה החסרה לישויות שיושבות על טבלה ייעודית
 *
 * ⚠️ **הבאג שזה מתקן נוצר ב-09.08.2026 ונתפס על ידי גל תוך שעה.**
 *
 * באותו יום נרשמו שמונה ישויות מעל טבלאות ייעודיות (`skills`, `pm_team`,
 * `knowledge_departments`, `knowledge_streams`, `comm_messages`,
 * `calendar_events`, `call_logs`, `closure_reason_taxonomy`) עם
 * `origin: "marketplace"` ב-`entitySourceResolver`. הרישום היה נכון — אבל
 * `fetchNotes()` הכיר **שני ענפים בלבד**:
 *
 *   origin === 'fireberry-mirror'  →  fb_records
 *   כל השאר                        →  vb_records
 *
 * ולכן כל שמונה הישויות נפלו לענף `vb_records`, שבו אין להן אף שורה,
 * והציגו רשימה ריקה. גל: *"ראיתי רק חלק ולא כולם טוענים."* 145 הודעות,
 * 66 סיבות סגירה ו-11 חברי צוות היו שם כל הזמן.
 *
 * **הלקח שנרשם:** רישום ישות אינו שלם עד שיש **מסלול קריאה** מהמקור אל
 * `NoteRecord`. שורה ב-`entity_types` + רשומה ב-resolver מגדירות *איפה*
 * המידע; בלי ממפה, המסך עדיין לא יודע *איך* לקרוא אותו.
 *
 * ⚠️ הממפה **גנרי בכוונה** — הוא נגזר מתצורת ה-resolver ולא מקודד שום
 * טבלה. עדשה תשיעית לא תדרוש קוד חדש.
 *
 * קריאה בלבד. כתיבה חוזרת לטבלת המקור אינה נתמכת בשלב הזה.
 */

import type { NoteRecord } from "@/lib/entities/types";
import type { EntitySource } from "@/lib/internal-tools/entitySourceResolver";

type Row = Record<string, unknown>;

const str = (v: unknown): string | undefined =>
  v === null || v === undefined ? undefined : String(v);

export function marketplaceRowToNoteRecord(row: Row, source: EntitySource): NoteRecord {
  const idCol = source.idColumn ?? "id";
  const id = str(row[idCol]) ?? "";

  // ⚠️ שם התצוגה נלקח מ-`displayField` ולא מניחוש. אם השדה ריק, מוטב
  // "(ללא שם)" מאשר שורה ריקה שנראית כמו תקלה.
  //
  // ⚠️ `displayJsonbKey` נוסף 18.09.2026 עבור טבלה שבה שם התצוגה יושב
  // בתוך jsonb ולא בעמודה שטוחה — entity_types.label הוא כזה, וגם
  // pm_apps.name. בלי זה String() על אובייקט מחזיר "[object Object]",
  // שנראה כמו נתון ולא כמו תקלה. השדה הוכרז ב-resolver כבר קודם ולא
  // נקרא באף מסלול, ולכן שום עדשה קיימת אינה משנה התנהגות כאן.
  const jsonbTitle = source.displayJsonbKey
    ? str((row[source.displayJsonbKey.column] as Record<string, unknown> | null | undefined)
        ?.[source.displayJsonbKey.key])
    : undefined;
  const title = (jsonbTitle ?? str(source.displayField ? row[source.displayField] : undefined))?.trim();
  const subtitle = str(source.subtitleField ? row[source.subtitleField] : undefined);

  // כל מה שאינו המזהה נכנס ל-meta, כדי שהתצוגות יוכלו להציג עמודות בלי
  // שנצטרך להכיר מראש את הסכימה של כל טבלה.
  const meta: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (k === idCol) continue;
    meta[k] = v;
  }
  if (subtitle) meta.subtitle = subtitle;

  // חותמות זמן — כל טבלה נוקטת שם אחר, ולכן מנסים את המקובלים.
  const created =
    str(row.created_at) ?? str(row.createdAt) ?? str(row.occurred_at) ?? str(row.starts_at) ?? "";
  const edited = str(row.updated_at) ?? str(row.last_edited_at) ?? created;

  return {
    id,
    title: title || "(ללא שם)",
    // אין גוף TipTap לשורה שמקורה בטבלה ייעודית.
    content: null,
    record_type: source.slug,
    entity_type: source.slug,
    meta,
    status: str(row.status) ?? "",
    source: source.table ?? "marketplace",
    // ⚠️ `is_deleted` תמיד false: הסינון של שורות מחוקות נעשה בשאילתה,
    // ורק כשהמקור מצהיר על `deletedColumn` ב-entitySourceResolver.ts.
    // מקור שלא הצהיר — שורותיו המחוקות יגיעו לכאן ויוצגו כחיות. לכן כל
    // טבלה חדשה עם deleted_at חייבת להצהיר עליו. להחזיר כאן true היה
    // מסתיר שורות תקינות מטבלאות שאין להן מחיקה רכה בכלל.
    is_deleted: false,
    created_by: str(row.created_by) ?? null,
    created_at: created,
    last_edited_at: edited,
  };
}
